import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PropertyDetails } from '../../../../core/models/property-details.model';

type PaymentPlan = PropertyDetails['payment_plans'][number];
type SelectedPlanSummary = PropertyDetails['selected_plan_summary'];

@Component({
  selector: 'app-payment-plans',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      id="payment-plans"
      class="surface-card p-6 md:p-8 scroll-mt-24 antialiased select-none"
    >
      <div class="flex items-center gap-3 mb-6 pb-3 border-b border-dashed border-border-subtle">
        <div
          class="w-8 h-8 rounded-xl bg-primary/10 flex items-center justify-center text-primary shadow-sm border border-primary/20"
        >
          <i class="fa-solid fa-calculator text-xs"></i>
        </div>
        <div>
          <h4 class="text-[10px] font-black uppercase tracking-widest text-text-muted font-mono">
            مرونة مالية
          </h4>
          <h2 class="text-lg font-black text-text-primary tracking-tight mt-0.5">
            خطط الدفع المتاحة
          </h2>
        </div>
      </div>

      @if (plans && plans.length) {
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          @for (plan of plans; track plan.name) {
            <div
              class="relative p-5 rounded-2xl cursor-pointer transition-all duration-300 border focus:outline-none flex flex-col justify-between group"
              [ngClass]="
                isPlanSelected(plan)
                  ? 'bg-surface-elevated border-primary shadow-primary-glow scale-[1.01]'
                  : 'bg-bg-secondary border-border hover:border-primary/50 hover:bg-surface-hover hover:shadow-lg hover:-translate-y-0.5'
              "
              tabindex="0"
              role="button"
              (click)="selectPlan(plan)"
              (keydown)="onPlanCardKeydown($event, plan)"
            >
              @if (plan.tag) {
                <div
                  class="absolute -top-2.5 left-5 px-2.5 py-0.5 bg-success/90 backdrop-blur-md text-white text-[9px] font-black uppercase tracking-wider font-mono rounded-md shadow-sm border border-success/30"
                >
                  {{ plan.tag }}
                </div>
              }

              @if (isPlanSelected(plan)) {
                <div
                  class="absolute -top-2.5 right-5 px-2 py-0.5 bg-primary/90 backdrop-blur-md text-white text-[9px] font-black uppercase tracking-wider font-mono rounded-md shadow-sm flex items-center gap-1 animate-fadeIn border border-primary/30"
                >
                  <i class="fa-solid fa-square-check text-[9px]"></i>
                  <span>نشط</span>
                </div>
              }

              <div class="text-center pt-2 relative z-10">
                <h3
                  class="text-sm font-black tracking-tight mb-2 transition-colors duration-300"
                  [class]="isPlanSelected(plan) ? 'text-primary' : 'text-text-primary group-hover:text-primary/80'"
                >
                  {{ plan.name }}
                </h3>

                @if (plan.discount) {
                  <div
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-black font-mono tracking-wide uppercase mb-4 transition-colors"
                    [ngClass]="
                      isPlanSelected(plan)
                        ? 'bg-primary/10 text-primary border border-primary/20'
                        : 'bg-success/10 border border-success/20 text-success'
                    "
                  >
                    <i class="fa-solid fa-percent text-[9px]"></i>
                    <span>توفير {{ plan.discount }}</span>
                  </div>
                }

                <div class="space-y-3 mb-4 text-center">
                  <div
                    class="pb-2 border-b border-dashed transition-colors"
                    [class]="isPlanSelected(plan) ? 'border-primary/20' : 'border-border-subtle group-hover:border-border'"
                  >
                    <span
                      class="block text-[10px] font-bold uppercase tracking-wider font-mono mb-0.5 text-text-muted"
                    >
                      المقدم
                    </span>
                    <span
                      class="text-sm font-black font-mono tracking-tight text-text-primary"
                    >
                      {{ plan.down_payment }}
                    </span>
                  </div>

                  @if (plan.monthly) {
                    <div
                      class="pb-2 border-b border-dashed transition-colors"
                      [class]="isPlanSelected(plan) ? 'border-primary/20' : 'border-border-subtle group-hover:border-border'"
                    >
                      <span
                        class="block text-[10px] font-bold uppercase tracking-wider font-mono mb-0.5 text-text-muted"
                      >
                        القسط الشهري
                      </span>
                      <span
                        class="text-sm font-black font-mono tracking-tight text-text-primary"
                      >
                        {{ plan.monthly }}
                      </span>
                    </div>
                  }

                  <div class="pt-1">
                    <span
                      class="block text-[10px] font-bold uppercase tracking-wider font-mono mb-0.5 transition-colors"
                      [class]="isPlanSelected(plan) ? 'text-primary/80' : 'text-text-muted'"
                    >
                      السعر النهائي
                    </span>
                    <span
                      class="text-lg font-black font-mono tracking-tight text-text-primary"
                    >
                      {{ plan.final_price }}
                    </span>
                  </div>
                </div>
              </div>

              <p
                class="text-[10px] font-medium text-center leading-normal mt-3 pt-3 border-t transition-colors"
                [class]="
                  isPlanSelected(plan)
                    ? 'text-text-secondary border-primary/20'
                    : 'text-text-muted border-border-subtle group-hover:border-border'
                "
              >
                {{ plan.note }}
              </p>
            </div>
          }
        </div>
      } @else {
        <div
          class="py-10 flex flex-col items-center justify-center border border-dashed border-border bg-bg-secondary/40 rounded-2xl text-center font-mono px-4 mb-6"
        >
          <div
            class="w-12 h-12 rounded-2xl bg-surface-elevated flex items-center justify-center text-text-muted mb-4 border border-border-subtle shadow-sm"
          >
            <i class="fa-solid fa-wallet text-lg"></i>
          </div>
          <h4 class="text-xs font-black text-text-secondary uppercase tracking-wider mb-2">
            الخطط غير متاحة
          </h4>
          <p
            class="text-[11px] font-medium text-text-muted max-w-[280px] leading-relaxed normal-case font-sans"
          >
            لم يتم نشر هياكل الدفع واستراتيجيات التمويل لهذه الوحدة.
          </p>
        </div>
      }

      <div class="p-5 bg-surface-elevated rounded-2xl border border-border transition-all shadow-inner relative overflow-hidden">
        <div class="absolute -right-10 -bottom-10 w-32 h-32 bg-primary/5 rounded-full blur-2xl"></div>
        
        <div class="flex items-center gap-2 mb-4 pb-3 border-b border-border-subtle relative z-10">
          <div
            class="w-6 h-6 rounded-lg bg-success/10 border border-success/20 flex items-center justify-center text-success shrink-0"
          >
            <i class="fa-solid fa-circle-check text-[11px]"></i>
          </div>
          <span class="text-xs font-bold text-text-secondary flex items-center gap-2">
            الخطة المحددة:
            <strong
              class="font-black text-primary font-mono bg-primary/10 px-2.5 py-1 rounded-lg border border-primary/20"
            >
              {{ selectedPlan?.name || 'في انتظار التحديد' }}
            </strong>
          </span>
        </div>

        <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs font-mono relative z-10">
          <div>
            <div class="text-[10px] font-bold uppercase tracking-wider text-text-muted mb-1">
              المقدم
            </div>
            <div class="font-black text-text-primary text-sm">
              {{ selectedPlan?.down_payment || '—' }}
            </div>
          </div>
          <div>
            <div class="text-[10px] font-bold uppercase tracking-wider text-text-muted mb-1">
              مدة الخطة
            </div>
            <div class="font-black text-text-primary text-sm">{{ selectedPlan?.duration || '—' }}</div>
          </div>
          <div>
            <div class="text-[10px] font-bold uppercase tracking-wider text-text-muted mb-1">
              القسط الشهري
            </div>
            <div class="font-black text-text-primary text-sm">
              {{ selectedPlan?.monthly_installment || '—' }}
            </div>
          </div>
          <div>
            <div class="text-[10px] font-bold uppercase tracking-wider text-text-muted mb-1">
              الخصم المطبق
            </div>
            <div class="font-black text-success text-sm">
              {{ selectedPlan?.discount || '0%' }}
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class PaymentPlansComponent {
  @Input() plans: PaymentPlan[] = [];
  @Input() selectedPlan: SelectedPlanSummary | null = null;
  @Output() planSelected = new EventEmitter<PaymentPlan>();

  selectPlan(plan: PaymentPlan): void {
    this.planSelected.emit(plan);
  }

  onPlanCardKeydown(event: KeyboardEvent, plan: PaymentPlan): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.selectPlan(plan);
    }
  }

  isPlanSelected(plan: PaymentPlan): boolean {
    if (this.selectedPlan?.name) {
      return this.selectedPlan.name === plan.name;
    }
    return !!plan.selected;
  }
}
